'use client';

import Accordion from '@enlighten/design-system/dist/Accordion';
import AccordionDetails from '@enlighten/design-system/dist/AccordionDetails';
import AccordionSummary from '@enlighten/design-system/dist/AccordionSummary';
import Pagination from '@enlighten/design-system/dist/Pagination';
import PaginationItem from '@enlighten/design-system/dist/PaginationItem';
import ArrowForwardIosSharp from '@mui/icons-material/ArrowForwardIosSharp';
import dayjs from 'dayjs';
import Link from 'next/link';
import { useEffect, useState } from 'react';

import type { WpPost } from '@/types/wp';

export interface NoticeListProps {
	total: number;
	page: number;
	data: Pick<WpPost, 'id' | 'date' | 'title' | 'content'>[];
}

export default function NoticeList({ total, page, data }: NoticeListProps) {
	const [expandedId, setExpandedId] = useState<number | null | undefined>();

	useEffect(() => {
		if (data.length > 0) {
			setExpandedId(data[0].id);
		}
	}, [data]);

	return (
		<>
			{data.map((item, i) => (
				<Accordion
					key={i}
					data-testid={`notice-${page}-${i}`}
					expanded={item.id === expandedId}
					square
					onChange={(_, expanded) => {
						setExpandedId(expanded ? item.id : undefined);
					}}
					sx={{
						width: '100%',
						boxShadow: 'none',
						'::before': {
							display: 'none',
						},
						'&.Mui-expanded': {
							margin: 0,
						},
					}}
				>
					<AccordionSummary
						expandIcon={<ArrowForwardIosSharp sx={{ fontSize: '0.9rem' }} />}
						sx={(theme) => ({
							flexDirection: 'row-reverse',
							alignItems: 'baseline',
							borderBottom: `1px solid ${theme.palette.grey['400']}`,
							minHeight: 48,
							paddingX: 2,
							'&.Mui-expanded': {
								minHeight: 48,
								'.MuiAccordionSummary-content': {
									margin: '12px 0',
								},
							},
							'& .MuiAccordionSummary-expandIconWrapper': {
								marginRight: 1,
								'&.Mui-expanded': {
									transform: 'rotate(90deg)',
								},
							},
							'.MuiAccordionSummary-content': {
								display: 'block',
							},
							'.title': {
								fontSize: 16,
								fontWeight: 700,
								lineHeight: 1.5,
							},
							'.date': {
								fontSize: 12,
								lineHeight: 2.2,
								color: 'grey.700',
								marginLeft: 1,
							},
						})}
					>
						<span className="title" dangerouslySetInnerHTML={{ __html: item.title.rendered }} />
						<span className="date">{dayjs(item.date).format('YYYY.MM.DD')}</span>
					</AccordionSummary>
					<AccordionDetails
						sx={(theme) => ({
							borderBottom: `1px solid ${theme.palette.grey['400']}`,
							padding: '30px',
							'.wp-content': {
								'& p': {
									fontSize: 16,
									lineHeight: 1.35,
									margin: 0,
								},
								'& ul': {
									lineHeight: 0,
									margin: '7px 0 1em 0',
									'& li': {
										lineHeight: 1.5,
									},
								},
								'.wp-block-details': {
									lineHeight: 0.6,
									margin: '10px 0',
								},
								'& a': {
									textDecoration: 'none',
								},
							},
						})}
					>
						<div className="wp-content" dangerouslySetInnerHTML={{ __html: item.content.rendered }} />
					</AccordionDetails>
				</Accordion>
			))}
			<Pagination
				size="small"
				count={total}
				page={page}
				// eslint-disable-next-line @typescript-eslint/no-unused-vars
				renderItem={({ onClick, ...item }) => (
					<Link href={`/notice?page=${item.page}`}>
						<PaginationItem {...item} />
					</Link>
				)}
				sx={{
					marginTop: 3,
				}}
			/>
		</>
	);
}
